import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import GlobalPlayer from "@/components/GlobalPlayer";
import SearchOverlay from "@/components/SearchOverlay";
import SocialRail from "@/components/SocialRail";
import ParticleField from "@/components/ParticleField";
import MaintenanceGate from "@/components/MaintenanceGate";
import UserQuickMenuPortal from "@/components/UserQuickMenu";
import { UserPreviewMount } from "@/components/UserProfilePreview";
import { usePlayer } from "@/context/PlayerContext";
import useAnalytics from "@/hooks/useAnalytics";

export default function Layout() {
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const { currentSong } = usePlayer();
  useAnalytics();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <MaintenanceGate>
    <div
      data-testid="app-layout"
      className="relative min-h-screen bg-[#020B1F] text-white overflow-x-hidden"
    >
      <div className="star-field" />
      <div className="pointer-events-none fixed inset-0 -z-0 overflow-hidden">
        <div className="light-rays" />
      </div>
      <div className="pointer-events-none fixed inset-0 -z-0">
        <div className="glow-orb glow-orb-neon animate-orb" style={{ width: 500, height: 500, top: "10%", left: "-10%", opacity: 0.25 }} />
        <div className="glow-orb glow-orb-deep animate-orb" style={{ width: 700, height: 700, bottom: "-20%", right: "-15%", opacity: 0.3, animationDelay: "3s" }} />
        <div className="glow-orb glow-orb-neon animate-orb" style={{ width: 350, height: 350, top: "50%", right: "20%", opacity: 0.15, animationDelay: "6s" }} />
      </div>
      <div className="pointer-events-none fixed inset-0 bg-grid opacity-30 -z-0" />
      <div className="hidden lg:block pointer-events-none fixed inset-0 -z-0 opacity-60">
        <ParticleField density={48} />
      </div>

      <Navbar onSearchOpen={() => setSearchOpen(true)} />
      <SocialRail />

      <main
        className="relative z-10 pt-20 sm:pt-24 transition-[padding] duration-300"
        style={{ paddingBottom: currentSong ? "100px" : "32px" }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      <Footer />
      <GlobalPlayer />
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      <UserQuickMenuPortal />
      <UserPreviewMount />
    </div>
    </MaintenanceGate>
  );
}
